import React, { useEffect, useRef } from "react";
import { Volume2, VolumeX, Waves } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/** Single-line karaoke ticker: the spoken sentence scrolls so the current word stays centred. Nothing else to read. */
export default function Narration({ words, wordIndex, speaking, sound, onToggleSound, supported, ambience, onToggleAmbience }) {
  const { t } = useLang();
  const trackRef = useRef(null);
  const wordRefs = useRef([]);
  const idx = Math.max(0, wordIndex);

  useEffect(() => {
    const track = trackRef.current; const el = wordRefs.current[idx];
    if (!track || !el) return;
    const box = track.parentElement.clientWidth;
    const target = el.offsetLeft + el.offsetWidth / 2 - box / 2;
    track.style.transform = `translateX(${-Math.max(0, Math.min(target, track.scrollWidth - box))}px)`;
  }, [idx, words]);

  return (
    <div className="absolute inset-x-0 bottom-0 z-20 px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-10 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" data-testid="story-narration">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 overflow-hidden rounded-2xl bg-black/45 backdrop-blur-md px-3 py-2.5" dir="auto">
          <div ref={trackRef} className="whitespace-nowrap font-display text-lg sm:text-xl leading-tight transition-transform duration-300 ease-out will-change-transform inline-block" data-testid="story-karaoke">
            {words.map((w, i) => (
              <span key={i} ref={(r) => { wordRefs.current[i] = r; }} className={`inline-block me-[0.35em] transition-colors duration-150 ${i === wordIndex ? "text-ochre scale-105" : i < wordIndex ? "text-white/45" : "text-white/90"}`}>{w}</span>
            ))}
          </div>
        </div>
        {supported && (
          <button onClick={onToggleSound} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} className="pointer-events-auto shrink-0 w-11 h-11 rounded-full bg-black/45 backdrop-blur-md text-white flex items-center justify-center hover:bg-ochre transition-colors" aria-label={sound ? t("story_stop") : t("story_listen")} data-testid="story-sound-toggle">
            {sound ? <Volume2 className={`w-5 h-5 ${speaking ? "animate-pulse" : ""}`} /> : <VolumeX className="w-5 h-5" />}
          </button>
        )}
        {onToggleAmbience && (
          <button onClick={onToggleAmbience} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} className={`pointer-events-auto shrink-0 w-11 h-11 rounded-full backdrop-blur-md flex items-center justify-center transition-colors ${ambience ? "bg-ochre text-white" : "bg-black/45 text-white/70"}`} aria-label={t("story_ambience")} aria-pressed={ambience} data-testid="story-ambience-toggle">
            <Waves className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
